/**
 * @workchat/shared-types/admin-org-import — Admin Organization Import (조직도 CSV) 의 cross-package SSOT.
 *
 * 본 모듈은 server (chat-server admin-org-import.routes.ts) + frontend (admin 조직도 import UI /
 * 매뉴얼 페이지) 가 공유하는 단일 정의. cycle 95 (HR Phase 5) 신규.
 *
 * admin-bulk-import.ts (User CSV) 와 동형 패턴 — 같은 string literal 이 4 곳(server union /
 * frontend union / DOC Record / 매뉴얼 표) 에 hardcode 되어 silent drift 나는 것을 차단한다:
 *   1. server / frontend 의 union 을 `OrgImportErrorCode` 로 단일화
 *   2. 한국어 doc (meaning + nextAction) 을 `ORG_IMPORT_CODE_DOC` Record 에서 SSOT
 *   3. KNOWN_ORG_IMPORT_CODES 를 `Object.keys(ORG_IMPORT_CODE_DOC)` 에서 derive
 *
 * chat-server 는 ESM/CJS interop issue 로 본 모듈을 직접 import 하지 않고 union 을 mirror hardcode 하며,
 * `scripts/check-org-import-codes-drift.sh` (npm `check:org-import-codes`) 가 양쪽 literal 을 grep 비교해
 * CI 에서 drift 를 차단한다 (admin-bulk-import.ts 의 `check:bulk-import-codes` 와 동형).
 */

/** server OrgImportRowError.code 의 10 종 — 코드 추가 시 본 union 만 갱신. */
export type OrgImportErrorCode =
  | 'DIVISION_NAME_MISSING'
  | 'DIVISION_NAME_TOO_LONG'
  | 'TEAM_NAME_MISSING'
  | 'TEAM_NAME_TOO_LONG'
  | 'MEMBER_EMAIL_INVALID'
  | 'MEMBER_NOT_FOUND'
  | 'DUPLICATE_MEMBERSHIP_IN_CSV'
  | 'SORT_ORDER_INVALID'
  | 'FK_VIOLATION'
  | 'CREATE_FAILED';

export interface OrgImportCodeDoc {
  /** 코드의 의미 — 사용자 가시 한국어. 매뉴얼 표 + 기타 doc 의 baseline. */
  meaning: string;
  /** 다음 행동 — admin 이 어떻게 해결할지. 매뉴얼 표 의 후속 조치 컬럼. */
  nextAction: string;
}

/**
 * 10 코드의 한국어 doc (cross-package SSOT).
 *
 * Record<OrgImportErrorCode, OrgImportCodeDoc> 의 exhaustive 강제 — union 확장 시 entry 누락이
 * 컴파일 에러로 잡힘. declaration 순서 = 매뉴얼 표 표시 순서 (필수 누락 → 길이 → 멤버 → 중복 → DB 오류).
 */
export const ORG_IMPORT_CODE_DOC: Record<OrgImportErrorCode, OrgImportCodeDoc> = {
  DIVISION_NAME_MISSING:       { meaning: 'divisionName 컬럼이 비어있습니다 (필수)', nextAction: 'divisionName 컬럼 채워서 재업로드' },
  DIVISION_NAME_TOO_LONG:      { meaning: 'divisionName 100자 초과', nextAction: '짧은 본부명 사용' },
  TEAM_NAME_MISSING:           { meaning: 'teamName 컬럼이 비어있습니다 (필수)', nextAction: 'teamName 컬럼 채워서 재업로드' },
  TEAM_NAME_TOO_LONG:          { meaning: 'teamName 100자 초과', nextAction: '짧은 팀명 사용' },
  MEMBER_EMAIL_INVALID:        { meaning: 'memberEmail 형식 오류 또는 255자 초과', nextAction: 'memberEmail 형식·길이 재확인 (비우면 구조만 등록)' },
  MEMBER_NOT_FOUND:            { meaning: '같은 institution 에 해당 email 사용자가 없습니다 (사용자 먼저 등록 필요)', nextAction: '사용자 일괄 등록(bulk-import) 후 조직도 재import' },
  DUPLICATE_MEMBERSHIP_IN_CSV: { meaning: '같은 CSV 안 동일 (본부·팀·email) 멤버십 중복', nextAction: 'CSV 안에서 중복 행 제거 (isLeader 충돌 방지)' },
  SORT_ORDER_INVALID:          { meaning: 'divisionSortOrder/teamSortOrder 가 0 이상 정수가 아님', nextAction: 'sortOrder 컬럼을 0 이상 정수로 또는 비우기' },
  FK_VIOLATION:                { meaning: 'foreign key 제약 위반 (server 로그 참조)', nextAction: 'server 로그 확인 후 운영팀 문의' },
  CREATE_FAILED:               { meaning: '알 수 없는 server 오류', nextAction: 'server 로그 확인 후 운영팀 문의' },
};

/**
 * 10 코드 set — runtime 검사용. Object.keys(ORG_IMPORT_CODE_DOC) 에서 derive 로 split-brain 차단.
 * frontend 가 unknown code 분기 시 본 set 으로 has 검사.
 */
export const KNOWN_ORG_IMPORT_CODES: ReadonlySet<OrgImportErrorCode> = new Set(
  Object.keys(ORG_IMPORT_CODE_DOC) as OrgImportErrorCode[],
);
